import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, Briefcase, LayoutDashboard, Settings, ChevronDown, Crown, GraduationCap, UserCheck, ShieldCheck, ClipboardList, Clock, Library, BookMarked, ScrollText, HeartPulse, Mail, Star } from 'lucide-react';

const LOGO_URL = "/inasw-logo-2026.jpg";

const Navbar = () => {
  const { user, logout, isAuthenticated, isEmployer, isAdmin, isJobSeeker } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    if (isEmployer) return '/employer/dashboard';
    if (user?.user_type === 'supervisor') return '/supervisor/dashboard';
    return '/dashboard';
  };

  const isLandingPage = location.pathname === '/';
  // Homepage hero is now a light image — keep the nav solid/dark-text everywhere for readability.
  const navSolid = true;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
      navSolid ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo with hidden admin access */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="INASW Logo" className="h-12 w-12 lg:h-16 lg:w-16 rounded-full object-cover ring-2 ring-secondary/50 shadow-sm flex-shrink-0" />
              <div className="hidden sm:block leading-tight">
                <span className={`font-serif text-xl lg:text-2xl font-bold whitespace-nowrap ${
                  navSolid ? 'text-primary' : 'text-white'
                }`}>
                  I Need A Social Worker
                </span>
              </div>
            </Link>
            {/* Secret Admin Access - Crown Icon (only you know what this is) */}
            {isAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`ml-1 p-1 rounded-full transition-all hover:scale-110 ${
                      navSolid
                        ? 'text-secondary/50 hover:text-secondary'
                        : 'text-white/30 hover:text-white/70'
                    }`}
                    title=" "
                    data-testid="admin-crown-menu"
                  >
                    <Crown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="crown-admin-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Admin Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="crown-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className={`ml-1 p-1 rounded-full transition-all hover:scale-110 ${
                  navSolid
                    ? 'text-secondary/40 hover:text-secondary'
                    : 'text-white/25 hover:text-white/60'
                }`}
                title=" "
                data-testid="admin-crown-link"
              >
                <Crown className="w-4 h-4" />
              </Link>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {!isLandingPage && (
              <Link to="/">
                <Button
                  className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                  data-testid="nav-home-link"
                >
                  Home
                </Button>
              </Link>
            )}
            <Link to="/jobs">
              <Button
                className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                data-testid="nav-jobs-link"
              >
                Jobs
              </Button>
            </Link>
            <Link to="/for-employers">
              <Button
                className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                data-testid="nav-employers-link"
              >
                Employers
              </Button>
            </Link>
            <Link to="/community/ask-a-social-worker">
              <Button
                className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                data-testid="nav-ask-sw-link"
              >
                Ask a SW
              </Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9 flex items-center gap-1"
                  data-testid="nav-supervision-menu"
                >
                  Supervision
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                <DropdownMenuItem onClick={() => navigate('/find-a-supervisor')} data-testid="nav-find-supervisor">
                  <UserCheck className="w-4 h-4 mr-2" /> Find LCSW-S Supervisor
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/become-a-supervisor')} data-testid="nav-become-supervisor">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Become a Listed Supervisor
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/internships')} data-testid="nav-find-internship">
                  <GraduationCap className="w-4 h-4 mr-2" /> Find an Internship
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/post-an-internship')} data-testid="nav-post-internship">
                  <Briefcase className="w-4 h-4 mr-2" /> Post an Internship
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/internship-portal')} data-testid="nav-internship-portal">
                  <ClipboardList className="w-4 h-4 mr-2" /> Internship Portal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9 flex items-center gap-1"
                  data-testid="nav-databases-menu"
                >
                  Resources
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuItem onClick={() => navigate('/behavioral-health-directory')} data-testid="nav-behavioral-health">
                  <HeartPulse className="w-4 h-4 mr-2" /> Behavioral Health Forms &amp; Assessments
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/social-work-forms')} data-testid="nav-social-work-forms">
                  <ScrollText className="w-4 h-4 mr-2" /> Social Work Forms Center
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/advance-directives')} data-testid="nav-advance-directives">
                  <ScrollText className="w-4 h-4 mr-2" /> Advance Directives &amp; Legal Forms
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/community/resources')} data-testid="nav-community-resources">
                  <UserCheck className="w-4 h-4 mr-2" /> Community Resource Directory
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/parent-resource-center')} data-testid="nav-parent-resource-center">
                  <UserCheck className="w-4 h-4 mr-2" /> National Parent Resource Center
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/my-library')} data-testid="nav-my-library">
                  <Library className="w-4 h-4 mr-2" /> My INASW Library
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!isLandingPage && (
              <>
                <Link to="/license-renewal-center">
                  <Button
                    className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                    data-testid="nav-license-center-link"
                  >
                    License Center
                  </Button>
                </Link>
                <Link to="/private-practice">
                  <Button
                    className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                    data-testid="nav-private-practice-link"
                  >
                    Private Practice Directory
                  </Button>
                </Link>
                <Link to="/blog">
                  <Button
                    className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                    data-testid="nav-blog-link"
                  >
                    Blog
                  </Button>
                </Link>
                <Link to="/about">
                  <Button
                    className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                    data-testid="nav-about-link"
                  >
                    About
                  </Button>
                </Link>
              </>
            )}
            
            {isAuthenticated ? (
              isAdmin ? null : (
              <>
                <Link
                  to={getDashboardLink()}
                  className={`font-medium text-sm transition-colors ${
                    navSolid
                      ? 'text-gray-600 hover:text-primary'
                      : 'text-white/90 hover:text-white'
                  }`}
                  data-testid="nav-dashboard-link"
                >
                  Dashboard
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`flex items-center gap-2 ${
                        navSolid
                          ? 'text-gray-700 hover:text-primary hover:bg-gray-100'
                          : 'text-white hover:text-white hover:bg-white/10'
                      }`}
                      data-testid="user-menu-trigger"
                    >
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <span className="font-medium">{user?.name?.split(' ')[0]}</span>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="font-medium text-sm">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/my-inasw')} data-testid="menu-my-inasw">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      My INASW
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-library')} data-testid="menu-my-library">
                      <Library className="w-4 h-4 mr-2" />
                      My INASW Library
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-featured')} data-testid="menu-my-featured">
                      <Star className="w-4 h-4 mr-2" />
                      My Featured Status
                    </DropdownMenuItem>
                    {isJobSeeker && (
                      <DropdownMenuItem onClick={() => navigate('/my-invitations')} data-testid="menu-my-invitations">
                        <Mail className="w-4 h-4 mr-2" />
                        My Invitations
                      </DropdownMenuItem>
                    )}
                    {isJobSeeker && (
                      <DropdownMenuItem onClick={() => navigate('/dashboard/profile')} data-testid="menu-profile">
                        <User className="w-4 h-4 mr-2" />
                        My Profile
                      </DropdownMenuItem>
                    )}
                    {isEmployer && (
                      <DropdownMenuItem onClick={() => navigate('/employer/post-job')} data-testid="menu-post-job">
                        <Briefcase className="w-4 h-4 mr-2" />
                        Post a Job
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
              )
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button
                    className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                    data-testid="nav-login-btn"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    className="bg-secondary hover:bg-secondary-hover text-white font-medium text-[13px] px-2.5 h-9"
                    data-testid="nav-register-btn"
                  >
                    Create Account
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className={`w-6 h-6 ${navSolid ? 'text-gray-700' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${navSolid ? 'text-gray-700' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t shadow-lg rounded-b-lg animate-fade-in">
            <div className="px-4 py-4 space-y-3">
              {!isLandingPage && (
                <Link
                  to="/"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
              )}
              <Link
                to="/jobs"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Jobs
              </Link>
              <Link
                to="/for-employers"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Employers
              </Link>
              <Link
                to="/find-a-supervisor"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Find LCSW-S Supervisor
              </Link>
              <Link
                to="/internships"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Internships
              </Link>
              <Link
                to="/private-practice"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-private-practice"
              >
                Private Practice
              </Link>
              <Link
                to="/behavioral-health-directory"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-behavioral-health"
              >
                Behavioral Health Forms
              </Link>
              <Link
                to="/social-work-forms"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-social-work-forms"
              >
                Social Work Forms Center
              </Link>
              <Link
                to="/advance-directives"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Advance Directives &amp; Legal Forms
              </Link>
              <Link
                to="/community/resources"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Community Resources
              </Link>
              <Link
                to="/parent-resource-center"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                National Parent Resource Center
              </Link>
              <Link
                to="/community/ask-a-social-worker"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Ask a Social Worker
              </Link>
              <Link
                to="/license-renewal-center"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                License &amp; Renewal Center
              </Link>
              <Link
                to="/internship-portal"
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Internship Portal
              </Link>
              
              {isAuthenticated ? (
                isAdmin ? null : (
                <>
                  <Link
                    to={getDashboardLink()}
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/my-library"
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-my-library"
                  >
                    My INASW Library
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium"
                  >
                    Sign Out
                  </button>
                </>
                )
              ) : (
                <>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className="w-full bg-secondary hover:bg-secondary-hover text-white">
                      Create Account
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
